/* @moldura padrao */
/**
 * page-celular.tsx (autorizações) — a lista no celular. Rota `/autorizacoes`.
 *
 * QUEM USA: a mãe dele, e ele eventualmente. Decisão de 25/08: *"autorizações eu
 * quero uma tela específica para celular; quem vai usar é a minha mãe"*.
 *
 * POR QUE TELA PRÓPRIA, e não a tabela apertada. Foi medido: em 390px a tabela
 * mostrava `Título · Tipo · Val…` e parava — o valor cortado no meio, Solicitante
 * e Vínculo 454px fora, atrás de rolagem lateral. Numa tela de **aprovar
 * dinheiro**, o número pela metade é o pior lugar possível para uma tabela rolar.
 *
 * O QUE MUDA, e é só isto — nenhum dado novo, nenhuma regra nova:
 *
 *   - cartão por pedido, não linha de tabela: nada rola para o lado;
 *   - o VALOR é o que domina o cartão, porque é o que se decide;
 *   - some o que não decide nada no aparelho (mensagens, vínculo, quem abriu
 *     fica em texto de apoio) — quem quer o resto abre o pedido;
 *   - o filtro de status vira pílula, que se toca com o polegar. O de tipo saiu:
 *     em celular ninguém filtra por dois eixos, e ele continua no desktop.
 *
 * O IRMÃO de escritório é `page-completo.tsx`; quem escolhe entre os dois é
 * `page.tsx`. Convenção em `11-convencao-visual.md` §8.
 *
 * ⚠️ NÃO tem "Nova autorização". Criar é composição — pede formulário, valor,
 * vínculo, anexo — e a decisão 0 do visual diz que composição não nasce no
 * celular. Quem precisa criar usa o notebook.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import type { Autorizacao } from './shared';
import { STATUS, andamentoDaAutorizacao, badge, rotuloStatus, valorResumo, varianteStatus } from './shared';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

const TODOS = 'todos';

export function AutorizacoesCelularPage() {
  const navigate = useNavigate();
  const [lista, setLista] = useState<Autorizacao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [fStatus, setFStatus] = useState(TODOS);

  async function carregar() {
    setCarregando(true);
    setErro(null);
    try {
      const params = new URLSearchParams();
      if (fStatus !== TODOS) params.set('status', fStatus);
      const qs = params.toString();
      setLista(await api.get<Autorizacao[]>(`/autorizacoes${qs ? `?${qs}` : ''}`));
    } catch (err) {
      setErro(
        err instanceof ApiError
          ? err.message
          : 'Não foi possível carregar as autorizações.',
      );
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fStatus]);

  return (
    <CustomMolduraPagina
      titulo="Autorizações"
      descricao="O que está esperando decisão"
      semCaminho
    >

      {/* Filtro em pílula: alvo de dedo, e a lista inteira à vista sem abrir
          nada. `flex-shrink-0` + `text-nowrap` porque `flex-nowrap` sozinho
          ESPREME cada pílula até a palavra quebrar dentro dela. */}
      <div className="d-flex gap-2 overflow-auto pb-2 mb-3 flex-nowrap">
        <button
          className={`btn btn-sm flex-shrink-0 text-nowrap ${
            fStatus === TODOS ? 'btn-theme' : 'btn-outline-theme'
          }`}
          onClick={() => setFStatus(TODOS)}
        >
          Todas
        </button>
        {STATUS.map((s) => (
          <button
            key={s.value}
            className={`btn btn-sm flex-shrink-0 text-nowrap ${
              fStatus === s.value ? 'btn-theme' : 'btn-outline-theme'
            }`}
            onClick={() => setFStatus(s.value)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {erro && <div className="alert alert-danger py-2 small">{erro}</div>}

      {carregando ? (
        <p className="text-secondary small">
          <span className="spinner-border spinner-border-sm me-2" />
          Carregando…
        </p>
      ) : lista.length === 0 ? (
        <div className="card adminuiux-card">
          <div className="card-body text-center py-5">
            <i className="bi bi-patch-check fs-3 text-secondary d-block mb-2" />
            <p className="text-secondary mb-0">Nada esperando decisão.</p>
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {lista.map((a) => (
            <button
              key={a.id}
              type="button"
              className="card adminuiux-card text-start border-0 w-100"
              onClick={() => navigate(`/autorizacoes/${a.id}`)}
            >
              <div className="card-body py-3">
                {/* Linha de cima: o estado e a data. Curtos, e é o que diz se
                    isto ainda é problema de alguém. */}
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span className={`badge ${badge(varianteStatus(a.status))}`}>
                    {rotuloStatus(a.status)}
                  </span>
                  {andamentoDaAutorizacao(a) && (
                    <span className={`badge ${andamentoDaAutorizacao(a)!.cor}`}>
                      {andamentoDaAutorizacao(a)!.rotulo}
                    </span>
                  )}
                  <span className="small text-secondary ms-auto">
                    {a.criado_em ? formatarData(a.criado_em) : '—'}
                  </span>
                </div>

                {/* O título é o que ela procura, então é escuro e negrito —
                    nunca perde peso por estado. */}
                <p className="fw-semibold mb-1">{a.titulo}</p>

                {/* O VALOR domina o cartão: é o que se decide. */}
                <p className="h5 mb-1">{valorResumo(a)}</p>

                <p className="small text-secondary mb-0">
                  {a.tipo_nome ?? 'Sem tipo'}
                  {(a.solicitante_nome ?? a.registrante_nome) &&
                    ` · ${a.solicitante_nome ?? a.registrante_nome}`}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </CustomMolduraPagina>
  );
}
